import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { pricelistCaseReminderBundleSchema } from "./shapes";

/**
 * Prepare Pricelist Case Reminder
 *
 * Receives a single assignee's reminder bundle (one fan-out branch from Query
 * Due Pricelist Case Follow-ups), renders the due-follow-up digest
 * title/body — case number, vendor, waiting-on, how overdue, and a link to the
 * case page — and resolves that recipient's preferred delivery channels from
 * their saved notification preferences (falling back to `fallbackChannels`).
 *
 * Outputs map directly onto the input sockets of the Send Announcement step
 * running in input-driven mode: `recipientUserIds`, `title`, `body`,
 * `channels`. Only the user ID travels: the mailbox is resolved server-side
 * from the user's primary email at delivery.
 */
export const preparePricelistCaseReminderStep = defineStepConfig({
  type: "prepare_pricelist_case_reminder",
  name: "Prepare Pricelist Case Reminder",
  description:
    "Render the due-follow-up digest for one pricelist-case assignee and " +
    "resolve their channels. Feeds Send Announcement (input-driven mode).",
  category: "Data Transform",
  libraryModule: "ItemMaster",

  inputSchema: z.object({
    recipientReminder: pricelistCaseReminderBundleSchema.describe(
      "One assignee's reminder bundle. Connect the 'reminders' array output " +
        "of Query Due Pricelist Case Follow-ups here to fan out per recipient.",
    ),
  }),

  outputSchema: z.object({
    recipientUserIds: z
      .array(z.string().uuid())
      .describe("User recipients. Empty when the digest is email-only."),
    extraEmails: z
      .array(z.string().email())
      .describe("Mailboxes with no Gently user. Email only."),
    title: z.string().describe("Digest headline."),
    body: z.string().describe("Digest body listing the due cases."),
    channels: z
      .array(z.enum(["in_app", "email"]))
      .describe("The recipient's resolved delivery channels."),
  }),

  configSchema: z.object({
    fallbackChannels: z
      .array(z.enum(["in_app", "email"]))
      .min(1)
      .default(["in_app", "email"])
      .describe(
        "Channels requested when the recipient has not saved a pricelist-case " +
          "reminder channel preference. Defaults to in-app and email; " +
          "NotificationService still intersects with each user's " +
          "notificationDelivery preference (default in-app only).",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 seconds",
    retry: {
      maximumAttempts: 3,
      backoffCoefficient: 2,
      initialInterval: "5 seconds",
    },
  },
});
